// TUTORIAL-MODE: Pre-signup sneak peek for unauthenticated visitors.
// Hand-builds a 5-slide stream from Quadratic Equations Ch4 concept[0] (no points,
// no Firestore, no auth) and renders via EnhancedLearnMode with tutorialMode=true.
// When the user reaches the last slide, an end-of-tutorial signup modal shows.

import { useEffect, useState } from "react";
import { EnhancedLearnMode } from "./EnhancedLearnMode";
import { TutorialEndPrompt } from "./TutorialEndPrompt";
import { PreSignupQuestion } from "./PreSignupQuestion";
import { trackEvent } from "../analytics";
import { getFontFamily, TAB_GRADIENTS } from "../constants/colors";

const TUTORIAL_CHAPTER_JSON = "/data/enriched-v2/class-10-maths-ch4-quadratic-equations.json";

// Build the tutorial slide stream from concept[0].
// 6 slides total: Intro → Hook puzzle → Teaching card 1 → Teaching card 2 → Concept-check → Memory anchor.
function buildTutorialSlides(concept, lang) {
  const isHi = lang === "hi";
  const e = concept.enriched || {};
  const slides = [];

  // Slide 0 is now rendered by the standalone <PreSignupQuestion> phase (no shared
  // renderer involved). The tutorial proper begins with the real hook below.

  // 1. Real hook (with embedded MCQ "Think about this" puzzle)
  if (e.hook) {
    slides.push({
      type: "hook",
      emoji: e.hook.emoji,
      title: e.hook.title,
      content: e.hook.content,
      question: e.hook.question,
      options: e.hook.options,
      correct: e.hook.correct,
      whyWrong: Array.isArray(e.hook.whyWrong) ? e.hook.whyWrong : null,
      hookAnswer: e.hookAnswer,
    });
  }

  // 2 & 3. First two teaching cards — `type: "concept"` (not "concept-point") so the
  // renderer shows ALL points as a bulleted list in one slide. `reveal: false`
  // means no progressive-reveal — bullets visible immediately.
  const teachingCards = Array.isArray(e.concepts) ? e.concepts.slice(0, 2) : [];
  teachingCards.forEach((card) => {
    slides.push({
      type: "concept",
      title: card.title,
      icon: card.icon,
      points: card.points,
      reveal: false,
    });
  });

  // 4. First concept-check (from microQuiz[0])
  if (Array.isArray(e.microQuiz) && e.microQuiz[0]) {
    const q = e.microQuiz[0];
    slides.push({
      type: "concept-check",
      microQuizIndex: 0,
      question: q.question,
      questionHi: q.questionHi || null,
      options: q.options || [],
      optionsHi: Array.isArray(q.optionsHi) ? q.optionsHi : null,
      correct: q.correct ?? 0,
      explanation: q.explanation || "",
      explanationHi: q.explanationHi || null,
      whyWrong: Array.isArray(q.whyWrong) ? q.whyWrong : null,
      whyWrongHi: Array.isArray(q.whyWrongHi) ? q.whyWrongHi : null,
    });
  }

  // 5. Memory anchor — memorable closer
  if (e.memoryAnchor) {
    slides.push({
      type: "memory-anchor",
      emoji: e.memoryAnchor.emoji || "🧠",
      fact: e.memoryAnchor.fact || "",
      factHi: e.memoryAnchor.factHi || null,
      factAudio: e.memoryAnchor.factAudio || null,
    });
  }

  return slides;
}

export function TutorialMode({ lang = "en", isAuthenticated = false, onExit, onSignUp, onSignIn }) {
  const ff = getFontFamily(lang);
  const isHi = lang === "hi";
  const [slideData, setSlideData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [showEndModal, setShowEndModal] = useState(false);
  // PRE-SIGNUP-PHASE: the standalone <PreSignupQuestion> is shown first; only on
  // "Continue learning" do we advance into the EnhancedLearnMode tutorial proper.
  const [phase, setPhase] = useState("intro");

  // Synthetic profile (no real user) — supplies what EnhancedLearnMode reads off `profile`.
  const syntheticProfile = { uid: null, studentClass: 10, board: "CBSE", lang };

  useEffect(() => {
    let cancelled = false;
    trackEvent("tutorial_started");
    fetch(TUTORIAL_CHAPTER_JSON)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(chapter => {
        if (cancelled) return;
        const concept = chapter.concepts?.[0];
        if (!concept) throw new Error("No concept[0] in chapter JSON");
        const tutorialSlides = buildTutorialSlides(concept, lang);
        if (tutorialSlides.length === 0) throw new Error("No slides built — chapter JSON shape mismatch");
        setSlideData({
          chapter: chapter.chapter,
          chapterKey: chapter.chapterKey,
          subject: chapter.subject,
          class: chapter.class,
          // No questionBank, no chapter-priming, etc. — keeps EnhancedLearnMode's slide assembly to just our 5.
          concepts: [{
            name: concept.name,
            id: concept.id,
            slides: tutorialSlides,
          }],
        });
      })
      .catch(err => {
        console.error("[TutorialMode] failed to load chapter JSON:", err);
        if (!cancelled) setLoadError(err.message || "Could not load tutorial");
      });
    return () => { cancelled = true; };
  }, []);

  const handleTutorialEnd = () => {
    trackEvent("tutorial_completed");
    // Authenticated users (founder/QA replay) skip the signup prompt — they're already signed in.
    if (isAuthenticated) {
      onExit?.();
      return;
    }
    setShowEndModal(true);
  };

  const handleSkip = () => {
    trackEvent("tutorial_skipped");
    // Authenticated: exit straight to home. Unauthenticated: still show the conversion modal.
    if (isAuthenticated) {
      onExit?.();
      return;
    }
    setShowEndModal(true);
  };

  const handleSignUpClick = () => {
    trackEvent("tutorial_signup_clicked");
    onSignUp?.();
  };

  const handleSignInClick = () => {
    trackEvent("tutorial_signin_clicked");
    onSignIn?.();
  };

  const handleIntroContinue = () => {
    trackEvent("tutorial_intro_continued");
    // Unauthenticated visitors: signup modal (the whole pitch is the 3 slides).
    // Authenticated (founder/QA replay): exit straight to home — no second tutorial.
    if (isAuthenticated) {
      onExit?.();
    } else {
      handleTutorialEnd();
    }
  };

  // Loading state
  if (loadError) {
    return (
      <div style={{ minHeight: "calc(100vh / 0.92)", zoom: 0.92, background: "#E8F2F8", fontFamily: ff, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ textAlign: "center", maxWidth: 360 }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#1E2A4A", marginBottom: 6 }}>
            {isHi ? "ट्यूटोरियल लोड नहीं हो सका" : "Tutorial couldn't load"}
          </div>
          <div style={{ fontSize: 12, color: "#5C6B82", marginBottom: 16 }}>
            {isHi ? "कृपया sign-in से continue करें।" : "Please continue via sign-in."}
          </div>
          <button onClick={onSignIn} style={{
            padding: "10px 22px", borderRadius: 12, background: "#1A7A52",
            color: "white", border: "none", fontSize: 14, fontWeight: 600,
            cursor: "pointer", fontFamily: ff,
          }}>
            {isHi ? "लॉगिन" : "Sign in"}
          </button>
        </div>
      </div>
    );
  }

  if (!slideData) {
    return (
      <div style={{ minHeight: "calc(100vh / 0.92)", zoom: 0.92, background: "#E8F2F8", fontFamily: ff, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              width: 10, height: 10, borderRadius: "50%", background: "#1A7A52",
              animation: `td 1.2s ease-in-out ${i * 0.2}s infinite`,
            }} />
          ))}
        </div>
        <style>{`
          @keyframes td {
            0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
            40% { opacity: 1; transform: scale(1); }
          }
        `}</style>
      </div>
    );
  }

  // PRE-SIGNUP-PHASE: standalone premium question slide; the EnhancedLearnMode tutorial
  // mounts only after the user taps "Continue learning →".
  if (phase === "intro") {
    return (
      <>
        <PreSignupQuestion
          lang={lang}
          onContinue={handleIntroContinue}
          onSkip={handleSkip}
          onSignIn={isAuthenticated ? handleIntroContinue : handleSignInClick}
        />
        {showEndModal && (
          <TutorialEndPrompt
            lang={lang}
            onSignUp={handleSignUpClick}
            onSignIn={handleSignInClick}
            onClose={onExit}
          />
        )}
      </>
    );
  }

  return (
    <div style={{ position: "relative", minHeight: "calc(100vh / 0.92)", zoom: 0.92, background: TAB_GRADIENTS.study }}>
      {/* Top-right escape strip. Skip is always shown (its behavior branches on auth state).
          Sign in is only shown for unauthenticated users — auth'd users don't need to re-login. */}
      <div style={{
        position: "fixed", top: "calc(env(safe-area-inset-top, 0px) + 12px)", right: 12, zIndex: 100,
        display: "flex", gap: 8,
      }}>
        <button
          onClick={handleSkip}
          style={{
            padding: "6px 14px", borderRadius: 999,
            background: "rgba(255,255,255,0.85)",
            backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
            border: "1px solid rgba(30,42,74,0.10)",
            color: "#5C6B82", fontSize: 12, fontWeight: 600,
            cursor: "pointer", fontFamily: ff,
          }}
        >
          {isHi ? "छोड़ें" : "Skip"}
        </button>
        {!isAuthenticated && (
          <button
            onClick={handleSignInClick}
            style={{
              padding: "6px 14px", borderRadius: 999,
              background: "rgba(255,255,255,0.85)",
              backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
              border: "1px solid rgba(30,42,74,0.10)",
              color: "#1E2A4A", fontSize: 12, fontWeight: 600,
              cursor: "pointer", fontFamily: ff,
            }}
          >
            {isHi ? "लॉगिन" : "Sign in"}
          </button>
        )}
      </div>

      <EnhancedLearnMode
        slideData={slideData}
        svgGenerators={{}}
        profile={syntheticProfile}
        lang={lang}
        tutorialMode={true}
        onTutorialEnd={handleTutorialEnd}
        onBack={onExit}
        onHome={onExit}
        onPractice={() => { /* no-op in tutorial */ }}
      />

      {showEndModal && (
        <TutorialEndPrompt
          lang={lang}
          onSignUp={handleSignUpClick}
          onSignIn={handleSignInClick}
          onClose={onExit}
        />
      )}
    </div>
  );
}
